import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Video, BookOpen, Brain, Award, CheckCircle, Clock, Users, Target } from 'lucide-react';
import SEO from '../components/SEO';
import Breadcrumb from '../components/Breadcrumb';

const TradersExam = () => {
  const tradersStructuredData = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "CIRO Traders Exam Prep Course - Traders Licensing Examination",
    "description": "Comprehensive preparation for the CIRO Traders Licensing Examination. Master all 10 elements including trading rules, marketplaces, trade execution, and derivatives with expert-led video lessons, practice questions, and full mock exams.",
    "provider": {
      "@type": "EducationalOrganization",
      "name": "Vitta Finance Academy",
      "url": "https://www.vittafinance.ca"
    },
    "courseCode": "TRADERS-PREP",
    "educationalLevel": "Professional",
    "about": ["CIRO Traders Exam", "Traders Licensing Examination", "Trade Desk Certification"],
    "teaches": ["Trading rules", "Marketplaces", "Trade execution", "Derivatives trading", "Clearing and settlement", "Ethics and compliance"],
    "numberOfCredits": "100 questions in 2 hours",
    "occupationalCredentialAwarded": "Licensed Trader",
    "offers": {
      "@type": "Offer",
      "price": "199.00",
      "priceCurrency": "CAD",
      "availability": "https://schema.org/PreOrder"
    },
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": "online",
      "courseWorkload": "PT40H"
    }
  };

  const tradersFaqData = [
    {
      question: "What is the CIRO Traders Exam?",
      answer: "The CIRO Traders Licensing Examination is a mandatory proficiency requirement for individuals seeking to work as licensed traders at investment dealers in Canada. It covers 10 key elements including trading rules, marketplaces, trade execution, derivatives, and compliance."
    },
    {
      question: "How many questions are on the Traders Exam?",
      answer: "The Traders Exam has 100 multiple-choice questions that must be completed in 2 hours. You have 3 attempts allowed per exam."
    },
    {
      question: "How long should I study for the Traders Exam?",
      answer: "We recommend 4-6 weeks of dedicated study time, spending 2-3 hours daily. Focus especially on Trading Rules (20 questions) and Methods of Trading (16 questions) as these are the highest-weighted sections."
    },
    {
      question: "What is the passing score for the Traders Exam?",
      answer: "The passing score for the Traders Exam is determined by CIRO for each exam sitting. CIRO uses a standardized scoring method to ensure fairness across different exam versions."
    },
    {
      question: "How do I register for the Traders Exam?",
      answer: "You can register for the Traders Exam through CIRO's official website or through your sponsoring firm. Exams are proctored and can be taken remotely or in-person at designated testing centers."
    },
    {
      question: "What topics are covered on the Traders Exam?",
      answer: "The Traders Exam covers 10 elements: Regulatory Environment, Capital Formation, Role of Traders & Trade Execution, Marketplaces, Methods of Trading, Trading Rules, Trade Desk Function & Compliance, Derivatives Requirements, Clearing & Settlement, and Ethics & Confidentiality."
    },
    {
      question: "Can I retake the Traders Exam if I fail?",
      answer: "Yes, you have 3 attempts allowed per exam. There is typically a waiting period before you can reattempt. We recommend reviewing your weak areas before retaking."
    },
    {
      question: "Is the Traders Exam proctored?",
      answer: "Yes, the Traders Exam is proctored and can be taken either remotely or in-person at designated testing centers."
    }
  ];

  const examElements = [
    { num: 1, title: 'The Regulatory Environment', questions: 4, desc: 'Understanding CIRO rules and Canadian securities regulations' },
    { num: 2, title: 'Capital Formation', questions: 4, desc: 'Primary and secondary markets, IPOs, and capital raising' },
    { num: 3, title: 'Role of Traders & Trade Execution', questions: 10, desc: 'Trader responsibilities and execution best practices' },
    { num: 4, title: 'Marketplaces', questions: 14, desc: 'Exchange structures, market types, and trading venues' },
    { num: 5, title: 'Methods of Trading', questions: 16, desc: 'Order types, algorithmic trading, and execution strategies' },
    { num: 6, title: 'Trading Rules', questions: 20, desc: 'CIRO trading rules, short selling, and market integrity' },
    { num: 7, title: 'Trade Desk Function & Compliance', questions: 9, desc: 'Supervision, compliance, and trade desk operations' },
    { num: 8, title: 'Derivatives Requirements', questions: 9, desc: 'Options, futures, and derivative trading regulations' },
    { num: 9, title: 'Clearing & Settlement', questions: 6, desc: 'Trade settlement, clearing processes, and CDS' },
    { num: 10, title: 'Ethics & Confidentiality', questions: 8, desc: 'Professional conduct, conflicts of interest, and confidentiality' }
  ];

  return (
    <div style={{ paddingTop: '80px' }}>
      <SEO 
        title="CIRO Traders Exam Prep 2025 - Traders Licensing Examination Course | Pass Guaranteed"
        description="Pass the CIRO Traders Exam on your first attempt. Canada's comprehensive Traders Licensing Examination prep course. 100 questions, 2 hours, 10 elements covered. Expert video lessons, practice questions, mock exams. Prep for licensed trader certification."
        keywords="CIRO Traders Exam, Traders Licensing Examination, traders exam prep, traders exam syllabus, traders exam practice, traders exam study guide, pass traders exam, traders exam registration, traders exam tips, how to pass traders exam, traders exam format, traders exam fees, traders mock exam, traders question bank, licensed trader exam, CIRO traders certification, trade desk exam, trading rules exam"
        ogTitle="CIRO Traders Exam Prep Course - Pass Your Traders Licensing Examination"
        ogDescription="Complete Traders Exam preparation. Expert instructors, practice questions, mock exams. Master trading rules, marketplaces, and trade execution."
        structuredData={tradersStructuredData}
        faqData={tradersFaqData}
        pageType="course"
      />

      {/* Hero Section */}
      <section style={{
        padding: '60px 0',
        background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
        color: 'white'
      }}>
        <div className="container">
          <Breadcrumb items={[
            { label: 'Home', link: '/' },
            { label: 'Courses', link: '/courses' },
            { label: 'Traders Exam' }
          ]} />
          
          <h1 style={{
            fontSize: 'clamp(2.5rem, 5vw, 4rem)',
            fontWeight: 900,
            lineHeight: 1.1,
            marginBottom: '24px',
            color: 'white'
          }}>
            CIRO Traders Licensing Examination
          </h1>
          <p style={{
            fontSize: '1.25rem',
            marginBottom: '32px',
            maxWidth: '800px',
            opacity: 0.95
          }}>
            Complete exam prep for aspiring licensed traders. Master all 10 CIRO elements including trading rules, marketplaces, and trade execution.
          </p>
          <div style={{
            display: 'flex',
            gap: '20px',
            flexWrap: 'wrap'
          }}>
            <a href="https://a.co/d/0c400Tnw" target="_blank" rel="noopener noreferrer" className="btn-gold" data-testid="traders-amazon-hero-btn">
              <BookOpen size={20} style={{ marginRight: '8px' }} />
              Get Study Guide on Amazon
            </a>
            <Link to="/contact" className="btn-secondary" style={{
              backgroundColor: 'white',
              color: '#0d9488',
              borderColor: 'white'
            }}>
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section style={{
        padding: '40px 0',
        backgroundColor: 'var(--neutral-light)',
        borderBottom: '1px solid var(--border-light)'
      }}>
        <div className="container">
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: '30px',
            textAlign: 'center',
            maxWidth: '900px',
            margin: '0 auto'
          }}>
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 900, color: '#0d9488', marginBottom: '8px' }}>100</div>
              <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Questions</div>
            </div>
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 900, color: '#0d9488', marginBottom: '8px' }}>2 Hours</div>
              <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Exam Duration</div>
            </div>
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 900, color: '#0d9488', marginBottom: '8px' }}>10</div>
              <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>CIRO Elements</div>
            </div>
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 900, color: '#0d9488', marginBottom: '8px' }}>3</div>
              <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Attempts Allowed</div>
            </div>
            <div>
              <div style={{ fontSize: '2rem', fontWeight: 900, color: '#0d9488', marginBottom: '8px' }}>Proctored</div>
              <div style={{ fontSize: '0.9rem', color: 'var(--text-secondary)' }}>Remote or In-Person</div>
            </div>
          </div>
        </div>
      </section>

      {/* About Traders Exam */}
      <section style={{ padding: '96px 0' }}>
        <div className="container">
          <div style={{ maxWidth: '900px', margin: '0 auto' }}>
            <h2 className="heading-2" style={{ marginBottom: '24px' }}>About the Traders Exam</h2>
            <p className="body-medium" style={{ marginBottom: '20px', lineHeight: 1.8 }}>
              The <strong>CIRO Traders Licensing Examination</strong> is the mandatory proficiency requirement for anyone seeking to work as a <strong>licensed trader</strong> at investment dealers in Canada.
            </p>
            <p className="body-medium" style={{ marginBottom: '20px', lineHeight: 1.8 }}>
              Administered by <strong>CIRO (Canadian Investment Regulatory Organization)</strong>, this exam tests your knowledge of trading rules, market structures, trade execution, and compliance across 10 key elements. The exam can be taken either remotely or in-person at designated proctored testing centers.
            </p>
            <p className="body-medium" style={{ marginBottom: '20px', lineHeight: 1.8 }}>
              With the heaviest weighting on <strong>Trading Rules (20 questions)</strong> and <strong>Methods of Trading (16 questions)</strong>, mastering these areas is critical for exam success.
            </p>
          </div>
        </div>
      </section>

      {/* 10 CIRO Elements */}
      <section style={{ padding: '96px 0', backgroundColor: 'var(--neutral-light)' }}>
        <div className="container">
          <h2 className="heading-2" style={{ marginBottom: '48px', textAlign: 'center' }}>10 CIRO Elements Covered</h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '24px'
          }}>
            {examElements.map((element) => (
              <div key={element.num} style={{
                backgroundColor: 'white',
                padding: '32px',
                borderRadius: '8px',
                border: '1px solid var(--border-light)',
                transition: 'transform 0.3s ease, box-shadow 0.3s ease'
              }} className="card-hover-effect">
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '50%',
                    backgroundColor: '#0d9488',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.25rem',
                    fontWeight: 900,
                    flexShrink: 0
                  }}>
                    {element.num}
                  </div>
                  <div style={{
                    backgroundColor: '#ccfbf1',
                    color: '#0d9488',
                    padding: '4px 10px',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: 700
                  }}>
                    {element.questions} Questions
                  </div>
                </div>
                <h3 className="heading-5" style={{ marginBottom: '12px' }}>{element.title}</h3>
                <p className="body-small">{element.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Course Features */}
      <section style={{ padding: '96px 0' }}>
        <div className="container">
          <h2 className="heading-2" style={{ marginBottom: '48px', textAlign: 'center' }}>Complete Traders Exam Prep</h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '32px'
          }}>
            {[
              {
                icon: <Video size={40} color="#0d9488" />,
                title: 'Video Lectures',
                desc: 'Expert instructors break down complex trading concepts into easy-to-understand lessons'
              },
              {
                icon: <Brain size={40} color="#0d9488" />,
                title: 'Interactive Games',
                desc: 'Reinforce learning with engaging interactive games and simulations'
              },
              {
                icon: <BookOpen size={40} color="#0d9488" />,
                title: 'Practice Questions',
                desc: 'Hundreds of exam-style MCQs with detailed explanations for every answer'
              },
              {
                icon: <Award size={40} color="#0d9488" />,
                title: 'Full Mock Exams',
                desc: 'Realistic 100-question practice exams that mirror actual Traders Exam format'
              },
              {
                icon: <Clock size={40} color="#0d9488" />,
                title: 'Podcast Episodes',
                desc: 'Learn on-the-go with audio lessons covering all exam topics'
              },
              {
                icon: <Target size={40} color="#0d9488" />,
                title: 'Online Study Book',
                desc: 'Comprehensive digital study guide with all 10 elements covered in depth'
              }
            ].map((feature, idx) => (
              <div key={idx} style={{
                backgroundColor: 'var(--neutral-light)',
                padding: '32px',
                borderRadius: '8px',
                border: '1px solid var(--border-light)'
              }}>
                <div style={{ marginBottom: '16px' }}>{feature.icon}</div>
                <h3 className="heading-5" style={{ marginBottom: '12px' }}>{feature.title}</h3>
                <p className="body-small">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Study Materials */}
      <section style={{ padding: '96px 0', backgroundColor: 'var(--neutral-light)' }}>
        <div className="container">
          <h2 className="heading-2" style={{ marginBottom: '48px', textAlign: 'center' }}>Traders Exam Study Materials</h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '32px',
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            {/* Amazon Book */}
            <div style={{
              backgroundColor: 'white',
              padding: '32px',
              borderRadius: '12px',
              border: '2px solid #0d9488',
              display: 'flex',
              flexDirection: 'column'
            }} className="card-hover-effect">
              <div style={{
                display: 'inline-block',
                backgroundColor: '#ccfbf1',
                color: '#0d9488',
                padding: '6px 14px',
                borderRadius: '20px',
                fontSize: '0.75rem',
                fontWeight: 700,
                marginBottom: '16px',
                width: 'fit-content'
              }}>
                AVAILABLE NOW
              </div>
              <h3 className="heading-5" style={{ marginBottom: '16px' }}>Printed Traders Exam Study Guide</h3>
              <p className="body-small" style={{ marginBottom: '24px', flex: 1 }}>
                Comprehensive study guide covering all 10 CIRO elements with detailed explanations, practice questions, and exam strategies.
              </p>
              <div style={{ fontSize: '2.5rem', fontWeight: 900, color: '#0d9488', marginBottom: '16px' }}>CAD $299.99</div>
              <a 
                href="https://a.co/d/0c400Tnw" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="btn-primary" 
                style={{ width: '100%', backgroundColor: '#0d9488', borderColor: '#0d9488' }}
                data-testid="traders-amazon-btn"
              >
                <BookOpen size={18} style={{ marginRight: '8px' }} />
                Buy on Amazon
              </a>
            </div>

            {/* Video Course */}
            <div style={{
              backgroundColor: 'white',
              padding: '32px',
              borderRadius: '12px',
              border: '2px solid #d4af37',
              display: 'flex',
              flexDirection: 'column',
              position: 'relative'
            }} className="card-hover-effect">
              <div style={{
                position: 'absolute',
                top: '-12px',
                right: '20px',
                background: 'linear-gradient(135deg, #D4AF37 0%, #B8860B 100%)',
                color: 'white',
                padding: '6px 14px',
                borderRadius: '20px',
                fontSize: '0.75rem',
                fontWeight: 700
              }}>
                COMING SOON
              </div>
              <div style={{
                display: 'inline-block',
                backgroundColor: '#fef3c7',
                color: '#92400e',
                padding: '6px 14px',
                borderRadius: '20px',
                fontSize: '0.75rem',
                fontWeight: 700,
                marginBottom: '16px',
                width: 'fit-content'
              }}>
                ONE-TIME PAYMENT
              </div>
              <h3 className="heading-5" style={{ marginBottom: '16px' }}>Traders Exam Video Course</h3>
              <p className="body-small" style={{ marginBottom: '16px', flex: 1 }}>
                Complete video course with online book, video lectures, podcast, interactive games, practice questions, and mock exams.
              </p>
              <div style={{ marginBottom: '20px' }}>
                {['Online Study Book', 'Video Lectures', 'Podcast Episodes', 'Interactive Games', 'Practice Questions', 'Mock Exams'].map((feature, idx) => (
                  <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                    <CheckCircle size={16} color="#0d9488" />
                    <span style={{ fontSize: '0.875rem', color: '#475569' }}>{feature}</span>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: '2.5rem', fontWeight: 900, color: '#0d9488', marginBottom: '8px' }}>CAD $199</div>
              <p style={{ fontSize: '0.8rem', color: '#64748b', marginBottom: '16px' }}>One-time payment - Lifetime access</p>
              <button 
                disabled
                style={{ 
                  width: '100%', 
                  padding: '14px 24px',
                  backgroundColor: '#e2e8f0',
                  color: '#64748b',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: 600,
                  cursor: 'not-allowed'
                }}
              >
                Coming Soon
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section style={{ padding: '96px 0', backgroundColor: 'var(--bg-page)' }}>
        <div className="container">
          <h2 className="heading-2" style={{ marginBottom: '16px', textAlign: 'center' }}>
            Frequently Asked Questions
          </h2>
          <p className="body-large" style={{ 
            marginBottom: '48px', 
            textAlign: 'center',
            color: 'var(--text-secondary)',
            maxWidth: '600px',
            margin: '0 auto 48px'
          }}>
            Everything you need to know about the CIRO Traders Exam
          </p>
          <div style={{
            maxWidth: '800px',
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '16px'
          }}>
            {tradersFaqData.map((faq, idx) => (
              <details 
                key={idx}
                style={{
                  backgroundColor: 'var(--bg-card)',
                  borderRadius: '12px',
                  border: '1px solid var(--border-light)',
                  overflow: 'hidden'
                }}
              >
                <summary style={{
                  padding: '20px 24px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '1.05rem',
                  color: 'var(--text-primary)',
                  listStyle: 'none',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  {faq.question}
                  <span style={{ 
                    fontSize: '1.5rem', 
                    color: '#0d9488',
                    fontWeight: 400
                  }}>+</span>
                </summary>
                <div style={{
                  padding: '0 24px 20px',
                  color: 'var(--text-secondary)',
                  lineHeight: 1.7
                }}>
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{
        padding: '96px 0',
        background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
        color: 'white',
        textAlign: 'center'
      }}>
        <div className="container">
          <h2 className="heading-2" style={{ marginBottom: '24px', color: 'white' }}>Ready to Pass Your Traders Exam?</h2>
          <p style={{ 
            fontSize: '1.25rem',
            lineHeight: 1.7,
            marginBottom: '40px', 
            maxWidth: '700px', 
            margin: '0 auto 40px',
            color: 'white',
            fontWeight: 500
          }}>
            Start your preparation today with our comprehensive study guide available on Amazon
          </p>
          <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="https://a.co/d/0c400Tnw" target="_blank" rel="noopener noreferrer" className="btn-gold" data-testid="traders-cta-amazon-btn">
              <BookOpen size={20} style={{ marginRight: '8px' }} />
              Get Study Guide - $299.99
            </a>
            <Link to="/contact" className="btn-secondary" style={{ backgroundColor: 'white', color: '#0d9488', borderColor: 'white' }}>
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TradersExam;
